import { useCallback, useEffect, useRef, useState } from 'react';
import { loadSavedMasterDiff, removeSavedMasterDiff } from './savedMasterDiff';

// How often a Master Note run in progress is checked while the chart is open.
export const RUNNING_POLL_MS = 4000;

// Owns the one-per-user-per-document saved Master Note for the open chart.
// Lives above ProcessModal because two surfaces need the same answer: the
// modal (restore banner) and the chart header (the badge that tells you a
// Master Note is waiting before you ever open the modal).
//
// The backend keeps every Master Note run in it (see savedMasterDiff.js), so
// this hook only reads and discards: `savedDiff` is a ready note, `runningRun`
// a run still going (here, in another tab or on the phone; checked every few
// seconds until it finishes), and `failedRun` a run that failed with nothing
// earlier to fall back to.
export default function useSavedMasterDiff({ documentPath, schema }) {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Guards the async load against a chart switch landing an older response on
  // the newer document (same pattern as usePatientDocument's isCurrent()).
  const reqRef = useRef(0);

  const refresh = useCallback(async () => {
    const req = reqRef.current + 1;
    reqRef.current = req;
    if (!documentPath) {
      setRecord(null);
      setLoading(false);
      return null;
    }
    setLoading(true);
    try {
      const next = await loadSavedMasterDiff({ documentPath, schema });
      if (reqRef.current !== req) return null; // a newer chart won
      setRecord(next || null);
      setError(null);
      return next || null;
    } catch (e) {
      // Keep what was shown: a failed read is not "nothing saved".
      if (reqRef.current === req) setError(e?.message || 'Could not load the saved Master Note.');
      return null;
    } finally {
      if (reqRef.current === req) setLoading(false);
    }
  }, [documentPath, schema]);

  useEffect(() => { void refresh(); }, [refresh]);

  const status = record?.status || (record?.proposedText ? 'ready' : null);
  useEffect(() => {
    if (status !== 'running') return undefined;
    const timer = setInterval(() => { void refresh(); }, RUNNING_POLL_MS);
    return () => clearInterval(timer);
  }, [status, refresh]);

  // Discard the saved Master Note (only run `requestId`'s, when given — e.g.
  // the one just accepted, so a newer run started elsewhere is left alone).
  const discard = useCallback(async (requestId) => {
    if (!documentPath) return false;
    setError(null);
    const ok = await removeSavedMasterDiff({ documentPath, schema, requestId: requestId || undefined });
    if (ok) {
      setRecord((current) => (!requestId || current?.requestId === requestId ? null : current));
      void refresh();
    } else {
      setError('Could not delete the saved Master Note.');
    }
    return ok;
  }, [documentPath, schema, refresh]);

  return {
    savedDiff: status === 'ready' ? record : null,
    runningRun: status === 'running' ? record : null,
    failedRun: status === 'failed' ? record : null,
    loading,
    error,
    discard,
    refresh,
  };
}
